import React, { useState, useEffect, useRef } from 'react';
import { usePost } from '../API';
import {
  useToast,
  Button,
  Input,
  TextArea,
  Heading,
  Card,
  CardHeader,
  CardBody
} from '@aidkitorg/component-library';
import { ReviewData } from '@aidkitorg/aidkit/lib/distro/review/distroReview';
import { useMarkdown } from '../Util';

interface DistroReviewDescriptionProps {
  reviewData: ReviewData | null;
  reviewId: string;
  onUpdate?: () => void;
  collapseDescription?: boolean;
}

export default function DistroReviewDescription({ 
  reviewData, 
  reviewId, 
  onUpdate,
  collapseDescription = true
}: DistroReviewDescriptionProps) {
  const patchDistroReview = usePost('/distro-review/patch');
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isUpdatingDescription, setIsUpdatingDescription] = useState(false);
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);
  const { toast } = useToast();
  const markdownDescription = useMarkdown(description);
  
  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionTextAreaRef = useRef<HTMLTextAreaElement>(null);

  const isOpen = reviewData?.status === 'review_pending' || reviewData?.status === 'approved';

  // Update title/description when reviewData changes
  useEffect(() => {
    if (!isEditingDescription) setDescription(reviewData?.description || '');
    if (!isEditingTitle) setTitle(reviewData?.title || '');
  }, [reviewData?.description, reviewData?.title, isEditingDescription, isEditingTitle]);

  // Focus input when title editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isEditingTitle]);

  // Focus textarea when description editing starts
  useEffect(() => {
    if (isEditingDescription && descriptionTextAreaRef.current) {
      descriptionTextAreaRef.current.focus();
    }
  }, [isEditingDescription]);

  // Title/Description editing handlers
  const handleEditDescriptionClick = () => {
    setIsEditingDescription(true);
  };

  const handleCancelDescriptionEdit = () => {
    setDescription(reviewData?.description || '');
    setIsEditingDescription(false);
  };

  const handleSaveDescription = async () => {
    if (!reviewId) return;
    
    setIsUpdatingDescription(true);
    try {
      const result = await patchDistroReview({ reviewId, description });
      if (result.error) {
        toast({
          description: result.error,
          variant: 'error'
        });
      } else {
        toast({
          description: 'Description updated successfully',
          variant: 'success',
          duration: 3000
        });
        setIsEditingDescription(false);
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error updating description:', error);
      toast({
        description: 'Failed to update description',
        variant: 'error'
      });
    } finally {
      setIsUpdatingDescription(false);
    }
  };

  const handleEditTitleClick = () => {
    setIsEditingTitle(true);
  };

  const handleCancelTitleEdit = () => {
    setTitle(reviewData?.title || '');
    setIsEditingTitle(false);
  };

  const handleSaveTitle = async () => {
    if (!reviewId) return;
    
    setIsUpdatingTitle(true);
    try {
      const result = await patchDistroReview({ reviewId, title });
      if (result.error) {
        toast({
          description: result.error,
          variant: 'error'
        });
      } else {
        toast({
          description: 'Title updated successfully',
          variant: 'success',
          duration: 3000
        });
        setIsEditingTitle(false);
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error updating title:', error);
      toast({
        description: 'Failed to update title',
        variant: 'error',
      });
    } finally {
      setIsUpdatingTitle(false);
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isUpdatingTitle) {
      e.preventDefault();
      handleSaveTitle();
    }
  };

  return (
    <div className="mb-6">
      {/* Title Section */}
      <div className="mb-2 w-full">
        {isEditingTitle ? (
          <div className="flex flex-row items-center gap-3 w-full">
            <div className="flex-1 w-full">
              <Input
                ref={titleInputRef}
                label="Review Title"
                showLabel={false}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                placeholder="Add a short title for this review..."
                disabled={isUpdatingTitle}
              />
            </div>
            <div className="flex flex-row justify-end items-center gap-3">
              <Button
                onClick={handleCancelTitleEdit}
                variant="secondary"
                isDisabled={isUpdatingTitle}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveTitle}
                loading={isUpdatingTitle}
                variant="primary"
                isDisabled={isUpdatingTitle}
              >
                Save Title
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-row justify-between items-end w-full gap-3">
            {reviewData?.title ? (
              <Heading level="h1" size="h2" className="text-lg font-semibold text-gray-900 mb-0">{reviewData.title}</Heading>
            ) : (
              <div />
            )}
            {isOpen && <div className="flex flex-row items-center gap-3">
              {!reviewData?.description && (
                <Button
                  onClick={handleEditDescriptionClick}
                  variant="secondary"
                >
                  Add Description
                </Button>
              )}
              <Button
                onClick={handleEditTitleClick}
                variant="secondary"
              >
                {reviewData?.title ? 'Edit Title' : 'Add Title'}
              </Button>
            </div>}
          </div>
        )}
      </div>

      {/* Description Section */}
      <div className="mb-6">
        
        
        {isEditingDescription ? (
          <div className="space-y-4 mb-10">
            <TextArea
              ref={descriptionTextAreaRef}
              label="Review Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description for this review..."
              rows={10}
              className="w-full"
              disabled={isUpdatingDescription}
            />
            <div className="flex justify-end gap-3">
              <Button
                onClick={handleCancelDescriptionEdit}
                variant="secondary"
                isDisabled={isUpdatingDescription}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveDescription}
                loading={isUpdatingDescription}
                variant="primary"
                isDisabled={isUpdatingDescription}
              >
                Save Description
              </Button>
            </div>
          </div>
        ) : (
          <div className="relative max-w-full">
            {reviewData?.description ? (
              <Card expandable={collapseDescription} collapsedHeight={collapseDescription ? 200 : undefined} className="w-full rounded-xl">
                <CardBody>
                  <div className="flex justify-between w-full">
                    <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {markdownDescription}
                    </div>
                    {isOpen && <Button
                      onClick={handleEditDescriptionClick}
                      variant="secondary"
                      className="max-h-fit"
                    >
                      Edit Description
                    </Button>}
                  </div>
                </CardBody>
              </Card>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
} 
